// components/TransferListTable.jsx
import { useMemo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Stack from '@mui/material/Stack';

function not(a, b) {
  return a.filter((value) => !b.some((x) => x === value));
}
function intersection(a, b) {
  return a.filter((value) => b.some((x) => x === value));
}
function union(a, b) {
  return [...a, ...not(b, a)];
}

export default function TransferListTable({
  leftItems = [],
  rightItems = [],
  onChange = () => {},
  titleLeft = '',
  titleRight = '',
  itemKey = 'id',
  itemLabel = 'name',
  height = 'calc(105vh - 220px)',
  showSearch = true,
  columns = null
}) {
  const [leftIds, setLeftIds] = useState([]);
  const [rightIds, setRightIds] = useState([]);
  const [checked, setChecked] = useState([]);
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');

  const [pageLeft, setPageLeft] = useState(0);
  const [rowsPerPageLeft, setRowsPerPageLeft] = useState(10);
  const [pageRight, setPageRight] = useState(0);
  const [rowsPerPageRight, setRowsPerPageRight] = useState(10);

  const allMap = useMemo(() => {
    const m = new Map();
    [...leftItems, ...rightItems].forEach((it) => {
      const id = String(it?.[itemKey]);
      m.set(id, it);
    });
    return m;
  }, [leftItems, rightItems, itemKey]);

  useEffect(() => {
    const newLeft = (leftItems || []).map((it) => String(it?.[itemKey]));
    const newRight = (rightItems || []).map((it) => String(it?.[itemKey]));

    setLeftIds((prev) => (prev.join(',') === newLeft.join(',') ? prev : newLeft));
    setRightIds((prev) => (prev.join(',') === newRight.join(',') ? prev : newRight));
    setChecked((prev) => prev.filter((id) => newLeft.includes(id) || newRight.includes(id)));
  }, [leftItems, rightItems, itemKey]);

  const effectiveColumns = useMemo(() => {
    if (Array.isArray(columns) && columns.length > 0) return columns;
    return [
      { field: itemLabel, label: itemLabel },
      { field: itemKey, label: itemKey }
    ];
  }, [columns, itemLabel, itemKey]);

  const itemMatchesSearch = useCallback(
    (item, search) => {
      if (!search) return true;
      const q = String(search).toLowerCase();
      for (const col of effectiveColumns) {
        const val = col.field ? (item?.[col.field] ?? '') : '';
        if (String(val).toLowerCase().includes(q)) return true;
      }
      return false;
    },
    [effectiveColumns]
  );

  const filteredLeftIds = useMemo(
    () =>
      leftIds.filter((id) => {
        const it = allMap.get(id);
        if (!it) return false;
        return itemMatchesSearch(it, searchLeft);
      }),
    [leftIds, allMap, searchLeft, itemMatchesSearch]
  );

  const filteredRightIds = useMemo(
    () =>
      rightIds.filter((id) => {
        const it = allMap.get(id);
        if (!it) return false;
        return itemMatchesSearch(it, searchRight);
      }),
    [rightIds, allMap, searchRight, itemMatchesSearch]
  );

  useEffect(() => {
    const maxLeftPage = Math.max(0, Math.ceil(filteredLeftIds.length / rowsPerPageLeft) - 1);
    if (pageLeft > maxLeftPage) setPageLeft(maxLeftPage);
  }, [filteredLeftIds.length, rowsPerPageLeft, pageLeft]);

  useEffect(() => {
    const maxRightPage = Math.max(0, Math.ceil(filteredRightIds.length / rowsPerPageRight) - 1);
    if (pageRight > maxRightPage) setPageRight(maxRightPage);
  }, [filteredRightIds.length, rowsPerPageRight, pageRight]);

  const leftChecked = useMemo(() => intersection(checked, leftIds), [checked, leftIds]);
  const rightChecked = useMemo(() => intersection(checked, rightIds), [checked, rightIds]);

  const handleToggle = useCallback((id) => {
    setChecked((prev) => {
      const idx = prev.indexOf(id);
      if (idx === -1) return [...prev, id];
      const copy = [...prev];
      copy.splice(idx, 1);
      return copy;
    });
  }, []);

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked((c) => not(c, items));
    } else {
      setChecked((c) => union(c, items));
    }
  };

  const handleCheckedRight = () => {
    const moving = intersection(checked, leftIds);
    if (moving.length === 0) return;
    const newLeft = not(leftIds, moving);
    const newRight = rightIds.concat(moving);
    setLeftIds(newLeft);
    setRightIds(newRight);
    setChecked((c) => not(c, moving));
    onChange({ left: newLeft, right: newRight });
    if (pageLeft > 0 && Math.ceil(newLeft.length / rowsPerPageLeft) <= pageLeft)
      setPageLeft(Math.max(0, Math.ceil(newLeft.length / rowsPerPageLeft) - 1));
    if (pageRight > Math.ceil(newRight.length / rowsPerPageRight) - 1)
      setPageRight(Math.max(0, Math.ceil(newRight.length / rowsPerPageRight) - 1));
  };

  const handleCheckedLeft = () => {
    const moving = intersection(checked, rightIds);
    if (moving.length === 0) return;
    const newRight = not(rightIds, moving);
    const newLeft = leftIds.concat(moving);
    setLeftIds(newLeft);
    setRightIds(newRight);
    setChecked((c) => not(c, moving));
    onChange({ left: newLeft, right: newRight });
    if (pageRight > 0 && Math.ceil(newRight.length / rowsPerPageRight) <= pageRight)
      setPageRight(Math.max(0, Math.ceil(newRight.length / rowsPerPageRight) - 1));
    if (pageLeft > Math.ceil(newLeft.length / rowsPerPageLeft) - 1)
      setPageLeft(Math.max(0, Math.ceil(newLeft.length / rowsPerPageLeft) - 1));
  };

  const renderSide = (
    title,
    ids,
    filteredIds,
    searchValue,
    onSearchChange,
    onToggleAll,
    checkedCount,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage
  ) => {
    const safePage = Number.isFinite(page) ? page : 0;
    const safeRows = Number.isFinite(rowsPerPage) && rowsPerPage > 0 ? rowsPerPage : 10;
    const start = safePage * safeRows;
    const end = start + safeRows;
    const pageIds = filteredIds.slice(start, end);

    return (
      <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          sx={{ px: 2, py: 1 }}
          avatar={
            <Checkbox
              onClick={onToggleAll}
              checked={checkedCount === filteredIds.length && filteredIds.length !== 0}
              indeterminate={checkedCount !== filteredIds.length && checkedCount !== 0}
              disabled={filteredIds.length === 0}
              inputProps={{ 'aria-label': 'all items selected' }}
            />
          }
          title={<Typography variant="subtitle1">{title}</Typography>}
          subheader={`${checkedCount}/${ids.length} selected • ${filteredIds.length} shown`}
        />
        <Divider />
        {showSearch && (
          <Box sx={{ px: 2, py: 1 }}>
            <TextField
              value={searchValue}
              onChange={(e) => {
                onSearchChange(e.target.value);
                setPage(0);
              }}
              placeholder="Search..."
              size="small"
              fullWidth
            />
          </Box>
        )}

        <TableContainer sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <Table stickyHeader size="small" aria-label={`${title} table`}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                {effectiveColumns.map((col) => (
                  <TableCell key={col.field ?? col.label}>{col.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {pageIds.map((id) => {
                const item = allMap.get(id);
                const isChecked = checked.includes(id);
                return (
                  <TableRow key={id} hover onClick={() => handleToggle(id)} selected={isChecked} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={isChecked} />
                    </TableCell>
                    {effectiveColumns.map((col) => {
                      const val = item ? (col.render ? col.render(item) : item?.[col.field]) : id;
                      return <TableCell key={`${id}-${col.field}`}>{val ?? '-'}</TableCell>;
                    })}
                  </TableRow>
                );
              })}
              {pageIds.length === 0 && (
                <TableRow>
                  <TableCell colSpan={effectiveColumns.length + 1}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No items
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 1 }}>
          <TablePagination
            component="div"
            count={filteredIds.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: height,
        minHeight: 400,
        display: 'flex',
        gap: 2,
        alignItems: 'stretch',
        justifyContent: 'center',
        flexDirection: { xs: 'column', md: 'row' }
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {renderSide(
          titleLeft,
          leftIds,
          filteredLeftIds,
          searchLeft,
          setSearchLeft,
          handleToggleAll(filteredLeftIds),
          numberOfChecked(filteredLeftIds),
          pageLeft,
          setPageLeft,
          rowsPerPageLeft,
          setRowsPerPageLeft
        )}
      </Box>

      <Box
        sx={{
          width: { xs: '100%', md: 72 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Stack spacing={1} direction="column" alignItems="center">
          <Button variant="outlined" size="small" onClick={handleCheckedRight} disabled={leftChecked.length === 0}>
            &gt;
          </Button>
          <Button variant="outlined" size="small" onClick={handleCheckedLeft} disabled={rightChecked.length === 0}>
            &lt;
          </Button>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {renderSide(
          titleRight,
          rightIds,
          filteredRightIds,
          searchRight,
          setSearchRight,
          handleToggleAll(filteredRightIds),
          numberOfChecked(filteredRightIds),
          pageRight,
          setPageRight,
          rowsPerPageRight,
          setRowsPerPageRight
        )}
      </Box>
    </Box>
  );
}

TransferListTable.propTypes = {
  leftItems: PropTypes.array,
  rightItems: PropTypes.array,
  onChange: PropTypes.func,
  titleLeft: PropTypes.string,
  titleRight: PropTypes.string,
  itemKey: PropTypes.string,
  itemLabel: PropTypes.string,
  height: PropTypes.number,
  showSearch: PropTypes.bool,
  columns: PropTypes.array
};
